/**
 * Routes: / (Today), /schedule, /lookup, /people, /volunteers, /events, /planning, /messages, /admin/*,
 * /settings, /login, and the public /link/:token. The server enforces roles on every route
 * (staticwebapp.config.json + withAuth); the client guard only keeps people on pages that are theirs.
 */
import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Role } from '@shared/roles';
import { useMe, type Me } from '@/auth/useMe';
import { AnnouncerProvider } from '@/components/Announcer';
import { Toaster } from '@/components/ui/use-toast';
import { REPLAY_WELCOME_EVENT, Welcome, returnFromWelcome, shouldShowWelcome } from '@/components/Welcome';
import { PrefsProvider } from '@/lib/prefs';
import { AppShell } from '@/app/AppShell';
import { TxProvider } from '@/lib/tx';
import { BookingProvider } from '@/features/booking/BookingProvider';
import { FinishProvider } from '@/features/finish/FinishProvider';
import { ProgramProvider } from '@/app/program';
import { HOUSEKEEPING_ONLY_ROLES, ROUTE_ROLES, TODAY_TAB_ROLES } from '@/app/nav';
import { VolunteerCardRedirect } from '@/app/VolunteerCardRedirect';
import { TodayPage } from '@/pages/Today';
import { ProgramHome } from '@/pages/ProgramHome';
import { LoginPage } from '@/pages/Login';
import { NoAccessPage, NotFoundPage, SettingsPage } from '@/pages/Placeholders';
import { EventsPage } from '@/pages/Events';
import { ComposeProvider } from '@/features/messages/ComposeProvider';
import { pingDispatcher, pingEngine } from '@/lib/messages';
import { prefetchToday } from '@/lib/queries';
import { serviceDateKey } from '@shared/dates';
import { KEEP_PICTURE_MS, keepMsFor } from '@shared/speed-1103';
import { useDayPlan } from '@/lib/queries';
import { PwaUpdate } from '@/components/PwaUpdate';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { BusyNotice } from '@/components/BusyNotice';
import { useMeExtras } from '@/lib/admin';
import { useCallingSync } from '@/lib/calling';
import { OfflineQueueProvider } from '@/features/offline/OfflineQueueProvider';
import { useSettingsSync } from '@/lib/my-settings';
import { usePrefs } from '@/lib/prefs';

/* Every screen but Today loads when first opened (Today is the first screen, so it ships in the main bundle). */
const LookupPage = lazy(() => import('@/pages/Lookup').then((m) => ({ default: m.LookupPage })));
const TenantPage = lazy(() => import('@/pages/TenantPage').then((m) => ({ default: m.TenantPage })));
const ReviewVisitPage = lazy(() => import('@/pages/ReviewVisit').then((m) => ({ default: m.ReviewVisitPage })));
const HomeVisitPage = lazy(() => import('@/pages/HomeVisit').then((m) => ({ default: m.HomeVisitPage })));
const SchedulePage = lazy(() => import('@/pages/Schedule').then((m) => ({ default: m.SchedulePage })));
const PeoplePage = lazy(() => import('@/pages/People').then((m) => ({ default: m.PeoplePage })));
const TenantLinkPage = lazy(() => import('@/pages/TenantLink').then((m) => ({ default: m.TenantLinkPage })));
const MessagesPage = lazy(() => import('@/pages/Messages').then((m) => ({ default: m.MessagesPage })));
const TemplatesPage = lazy(() => import('@/pages/admin/Templates').then((m) => ({ default: m.TemplatesPage })));
const WordingPreviewPage = lazy(() => import('@/pages/admin/WordingPreview').then((m) => ({ default: m.WordingPreviewPage })));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminIndex = lazy(() => import('@/pages/admin/AdminLayout').then((m) => ({ default: m.AdminIndex })));
const HealthPage = lazy(() => import('@/pages/admin/Health').then((m) => ({ default: m.HealthPage })));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettings').then((m) => ({ default: m.AdminSettingsPage })));
const ReviewQueuePage = lazy(() => import('@/pages/admin/ReviewQueue').then((m) => ({ default: m.ReviewQueuePage })));
const UsersPage = lazy(() => import('@/pages/admin/Users').then((m) => ({ default: m.UsersPage })));
const FixItPage = lazy(() => import('@/pages/admin/FixIt').then((m) => ({ default: m.FixItPage })));
const AuditPage = lazy(() => import('@/pages/admin/Audit').then((m) => ({ default: m.AuditPage })));
const InstallPage = lazy(() => import('@/pages/Install').then((m) => ({ default: m.InstallPage })));
const ServiceDaysPage = lazy(() => import('@/pages/admin/Slots').then((m) => ({ default: m.ServiceDaysPage })));
const PlanningPage = lazy(() => import('@/pages/Planning').then((m) => ({ default: m.PlanningPage })));
const HelpPage = lazy(() => import('@/pages/Help').then((m) => ({ default: m.HelpPage })));
const AboutPage = lazy(() => import('@/pages/admin/About').then((m) => ({ default: m.AboutPage })));
const ExportPage = lazy(() => import('@/pages/admin/About').then((m) => ({ default: m.ExportPage })));
const ReportsPage = lazy(() => import('@/pages/admin/Reports').then((m) => ({ default: m.ReportsPage })));
const ImportPage = lazy(() => import('@/pages/admin/Import').then((m) => ({ default: m.ImportPage })));
const TechTicketPage = lazy(() => import('@/pages/TechTicket').then((m) => ({ default: m.TechTicketPage })));
// 1.8.5 (D-1.8.5-1): the Tech Support tab
const TechSupportPage = lazy(() => import('@/pages/TechSupport').then((m) => ({ default: m.TechSupportPage })));
const EventPage = lazy(() => import('@/pages/EventPage').then((m) => ({ default: m.EventPage })));
const JoinPage = lazy(() => import('@/pages/Join').then((m) => ({ default: m.JoinPage })));
const OpenRsvpPage = lazy(() => import('@/pages/OpenRsvp').then((m) => ({ default: m.OpenRsvpPage })));
const EventRosterPage = lazy(() => import('@/pages/EventRoster').then((m) => ({ default: m.EventRosterPage })));
const HousekeepingNumbersPage = lazy(() => import('@/pages/HousekeepingNumbers').then((m) => ({ default: m.HousekeepingNumbersPage })));
const SurveyPage = lazy(() => import('@/pages/SurveyPage').then((m) => ({ default: m.SurveyPage })));
const SurveysPage = lazy(() => import('@/pages/Surveys').then((m) => ({ default: m.SurveysPage })));
const SurveyOfficePage = lazy(() => import('@/pages/SurveyOffice').then((m) => ({ default: m.SurveyOfficePage })));
const DocumentsPage = lazy(() => import('@/pages/Documents').then((m) => ({ default: m.DocumentsPage })));
const ImpactReportsPage = lazy(() => import('@/pages/Reports').then((m) => ({ default: m.ImpactReportsPage })));
const AnnouncementsPage = lazy(() => import('@/pages/Announcements').then((m) => ({ default: m.AnnouncementsPage })));
const EventsMonthPage = lazy(() => import('@/pages/EventsMonth').then((m) => ({ default: m.EventsMonthPage })));
// 1.8.2 (D-1.8.2-3): the house calendar
const CalendarPage = lazy(() => import('@/pages/Calendar').then((m) => ({ default: m.CalendarPage })));
const AnnouncementPage = lazy(() => import('@/pages/AnnouncementPage').then((m) => ({ default: m.AnnouncementPage })));
const VolunteersPage = lazy(() => import('@/pages/Volunteers').then((m) => ({ default: m.VolunteersPage })));
const VolunteerPage = lazy(() => import('@/pages/VolunteerPage').then((m) => ({ default: m.VolunteerPage })));

// 2 Nov 2026 night (D-1103-SPEED-2): an answer is kept 30 minutes, so a tab switch shows the last picture at once and
// refreshes it in the background. Admin → Settings "Show the last picture while it updates" = No puts it back to 5.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true, gcTime: KEEP_PICTURE_MS } },
});

/** Follows the switch (it rides along with the day plan, which the app has already read; this never asks for it). */
function PictureKeeper() {
  const plan = useDayPlan(false);
  const ms = keepMsFor(plan.data?.settings);
  useEffect(() => {
    const q = queryClient.getDefaultOptions().queries ?? {};
    if (q.gcTime !== ms) queryClient.setDefaultOptions({ ...queryClient.getDefaultOptions(), queries: { ...q, gcTime: ms } });
  }, [ms]);
  return null;
}

function Guard({ me, path, children }: { me: Me; path: string; children: ReactNode }) {
  // The crew's own view is Today (and their Settings) — and, 1.6.7, Review visit: read-only, their own crew's visits.
  const allowed = ROUTE_ROLES[path] as readonly Role[] | undefined;
  if (me.role === 'crew' && path !== '/' && path !== '/settings' && path !== '/help' && path !== '/visits/:appointmentId') return <Navigate to="/" replace />;
  // The technology associate (1.5.0): their own tickets, a ticket, and Help & Support; 1.8.5: the Tech Support tab, where
  // they open (`/` sends them there)
  if (me.role === 'tech' && path === '/') return <Navigate to="/tech" replace />;
  if (me.role === 'tech' && path !== '/tech' && path !== '/settings' && path !== '/help' && path !== '/tech/:appointmentId') return <Navigate to="/tech" replace />;
  if (allowed && (!me.role || !allowed.includes(me.role))) return <NoAccessPage />;
  return <>{children}</>;
}

/** Whenever a signed-in person loads or comes back to the app, the sender runs (it sends only what is due). */
function DispatchPing({ on }: { on: boolean }) {
  useEffect(() => {
    if (!on) return undefined;
    // after the first screen's own reads, not in the middle of them (1103, D-1103-SPEED-7): when the first screen's reads
    // have answered (something was fetched and nothing is still on its way), or after 10 seconds - whichever comes first
    let fired = false;
    let unsubscribe: (() => void) | null = null;
    const go = () => {
      if (fired) return;
      fired = true;
      unsubscribe?.();
      window.clearTimeout(first);
      pingDispatcher(true);
      pingEngine(true);
    };
    let sawFetch = false;
    unsubscribe = queryClient.getQueryCache().subscribe(() => {
      if (queryClient.isFetching() > 0) sawFetch = true;
      else if (sawFetch) window.setTimeout(go, 300);
    });
    const first = window.setTimeout(go, 10_000);
    const again = () => {
      if (document.visibilityState === 'visible') {
        pingDispatcher();
        pingEngine();
      }
    };
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      fired = true;
      unsubscribe?.();
      window.clearTimeout(first);
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [on]);
  return null;
}

/**
 * Once per session: GET /api/me records the sign-in (Admin → Users, "Someone can't sign in") and brings the
 * person's extras — the language the administrator set for them applies until they choose one under the gear.
 */
function MeSeen({ userId }: { userId: string }) {
  const me = useMeExtras();
  const { prefs, setPrefs } = usePrefs();
  // 1.5.7: "Hide my number when I call from the app" — this device's copy follows the account's
  useCallingSync();
  // 1.5.16: language, appearance, text size, the last program and the last Admin page follow the person too
  useSettingsSync();
  useEffect(() => {
    // the person's own saved language (1.5.16) comes before the one the administrator set for them
    const lang = me.data?.settings?.lang ? null : me.data?.extras.language;
    let saved = true;
    try {
      saved = localStorage.getItem(`abf.prefs.${userId}`) !== null;
    } catch {
      saved = true;
    }
    // only on a device where this person has not chosen anything under the gear yet
    // (the administrator's choice is not the person's own: it is applied here, not sent back as theirs)
    if (lang && !saved && prefs.lang !== lang) setPrefs({ lang, settingsUnsent: prefs.settingsUnsent });
  }, [me.data, prefs.lang, prefs.settingsUnsent, setPrefs, userId]);
  return null;
}

function RecordAttempt() {
  useMeExtras();
  return null;
}

function SignedIn() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  if (me.isLoading) {
    return (
      <p className="help" style={{ padding: 24 }} role="status">
        {t('app.loading')}
      </p>
    );
  }
  const data = me.data;
  if (!data?.principal) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (!data.role) {
    // Recorded all the same (GET /api/me): Admin → Users → "Someone can't sign in" shows the attempt.
    return (
      <main id="main" style={{ padding: 24 }}>
        <RecordAttempt />
        <NoAccessPage />
      </main>
    );
  }
  const g = (path: string, el: ReactNode) => (
    <Guard me={data} path={path}>
      {el}
    </Guard>
  );
  return (
    <TxProvider>
      <DispatchPing on={!!data.role} />
      <MeSeen userId={data.principal.userId} />
      <PictureKeeper />
      <OfflineQueueProvider role={data.role} userEmail={data.principal.userDetails ?? null}>
      <ComposeProvider role={data.role}>
      <ProgramProvider housekeepingOnly={HOUSEKEEPING_ONLY_ROLES.includes(data.role)} today={TODAY_TAB_ROLES.includes(data.role)}>
      <BookingProvider role={data.role}>
        <FinishProvider role={data.role}>
            <Routes>
              <Route element={<AppShell me={data} />}>
                <Route index element={g('/', <TodayPage me={data} />)} />
                {/* 25 Oct 2026 (D-1025-NAV): an address for each program. Companion and Reading show ProgramHome as before. */}
                <Route path="housekeeping" element={g('/housekeeping', <TodayPage me={data} />)} />
                <Route path="companion" element={g('/companion', <ProgramHome program="companion" me={data} />)} />
                <Route path="reading" element={g('/reading', <ProgramHome program="reading" me={data} />)} />
                <Route path="today" element={<Navigate to="/" replace />} />
                <Route path="book" element={<Navigate to="/housekeeping?book=1" replace />} />
                <Route path="lookup" element={g('/lookup', <LookupPage />)} />
                <Route path="schedule" element={g('/schedule', <SchedulePage />)} />
                <Route path="planning" element={g('/planning', <PlanningPage role={data.role} />)} />
                <Route path="planning/numbers" element={g('/planning/numbers', <HousekeepingNumbersPage role={data.role} />)} />
                <Route path="calendar" element={g('/calendar', <CalendarPage role={data.role} />)} />
                <Route path="people" element={g('/people', <PeoplePage role={data.role} />)} />
                <Route path="people/:tenantId" element={g('/people/:tenantId', <TenantPage />)} />
                <Route path="people/:tenantId/home-visit" element={g('/people/:tenantId/home-visit', <HomeVisitPage role={data.role} />)} />
                <Route path="visits/:appointmentId" element={g('/visits/:appointmentId', <ReviewVisitPage role={data.role} />)} />
                <Route path="volunteers" element={g('/volunteers', <VolunteerCardRedirect><VolunteersPage /></VolunteerCardRedirect>)} />
                <Route path="volunteers/:volunteerId" element={g('/volunteers/:volunteerId', <VolunteerPage />)} />
                <Route path="documents" element={g('/documents', <DocumentsPage />)} />
                <Route path="reports" element={g('/reports', <ImpactReportsPage />)} />
                <Route path="events" element={g('/events', <EventsPage role={data.role} />)} />
                <Route path="events/surveys" element={g('/events/surveys', <SurveysPage role={data.role} />)} />
                <Route path="events/surveys/:slug" element={g('/events/surveys/:slug', <SurveyOfficePage role={data.role} />)} />
                <Route path="events/month" element={g('/events/month', <EventsMonthPage role={data.role} />)} />
                <Route path="events/announcements" element={g('/events/announcements', <AnnouncementsPage role={data.role} />)} />
                <Route path="events/announcements/:id" element={g('/events/announcements/:id', <AnnouncementPage role={data.role} />)} />
                <Route path="events/:eventId" element={g('/events/:eventId', <EventPage role={data.role} />)} />
                <Route path="events/:eventId/roster" element={g('/events/:eventId/roster', <EventRosterPage role={data.role} />)} />
                <Route path="messages" element={g('/messages', <MessagesPage role={data.role} />)} />
                <Route path="settings" element={g('/settings', <SettingsPage me={data} />)} />
                <Route path="help" element={g('/help', <HelpPage me={data} />)} />
                <Route path="tech" element={g('/tech', <TechSupportPage me={data} />)} />
                <Route path="tech/:appointmentId" element={g('/tech/:appointmentId', <TechTicketPage />)} />
                <Route path="admin" element={g('/admin', <AdminLayout />)}>
                  <Route index element={<AdminIndex />} />
                  <Route path="health" element={<HealthPage />} />
                  <Route path="settings" element={<AdminSettingsPage />} />
                  <Route path="settings/preview/:code" element={<WordingPreviewPage />} />
                  <Route path="templates" element={<TemplatesPage />} />
                  {/* 1.6.9: Slots became Service days (reached from Settings → Closed days and service days); the old address still lands there */}
                  <Route path="service-days" element={<ServiceDaysPage />} />
                  <Route path="slots" element={<Navigate to="/admin/service-days" replace />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="fixit" element={<FixItPage />} />
                  <Route path="fix" element={<Navigate to="/admin/fixit" replace />} />
                  <Route path="review" element={<ReviewQueuePage />} />
                  <Route path="audit" element={<AuditPage />} />
                  <Route path="import" element={<ImportPage />} />
                  <Route path="export" element={<ExportPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
        </FinishProvider>
      </BookingProvider>
      </ProgramProvider>
      </ComposeProvider>
      </OfflineQueueProvider>
    </TxProvider>
  );
}

function PrefsForUser({ children }: { children: ReactNode }) {
  const me = useMe();
  return <PrefsProvider userId={me.data?.principal?.userId ?? null}>{children}</PrefsProvider>;
}

function Loading() {
  const { t } = useTranslation();
  return (
    <p className="help" style={{ padding: 24 }} role="status">
      {t('app.loading')}
    </p>
  );
}

function Root() {
  const location = useLocation();
  const isLink = location.pathname.startsWith('/link/') || location.pathname.startsWith('/t/') || location.pathname.startsWith('/e/') || location.pathname.startsWith('/s/') || location.pathname === '/join' || location.pathname === '/install';
  const [welcome, setWelcome] = useState(() => !isLink && shouldShowWelcome());
  useEffect(() => {
    const replay = () => setWelcome(true);
    window.addEventListener(REPLAY_WELCOME_EVENT, replay);
    return () => window.removeEventListener(REPLAY_WELCOME_EVENT, replay);
  }, []);
  return (
    <>
          <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/link/:token" element={<TenantLinkPage />} />
            <Route path="/t/:token" element={<TenantLinkPage />} />
            <Route path="/e/:slug" element={<OpenRsvpPage />} />
            {/* 13 Oct 2026: the volunteers' own link — never the tenants' */}
            <Route path="/e/:slug/v" element={<OpenRsvpPage side="volunteers" />} />
            <Route path="/s/:slug" element={<SurveyPage />} />
            {/* Eli, 15 Oct 2026: the open volunteer sign-up (the Volunteers page's QR code) */}
            <Route path="/join" element={<JoinPage />} />
            <Route path="/*" element={<SignedIn />} />
          </Routes>
          </Suspense>
      {welcome ? (
        <Welcome
          onDone={() => {
            setWelcome(false);
            window.setTimeout(returnFromWelcome, 0);
          }}
        />
      ) : null}
      <PwaUpdate />
    </>
  );
}

export default function App() {
  useEffect(() => {
    if (window.location.pathname === '/') prefetchToday(queryClient, serviceDateKey());
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <PrefsForUser>
        <AnnouncerProvider>
          <Toaster>
            <BrowserRouter>
              <ErrorBoundary>
                <Root />
              </ErrorBoundary>
              <BusyNotice />
            </BrowserRouter>
          </Toaster>
        </AnnouncerProvider>
      </PrefsForUser>
    </QueryClientProvider>
  );
}
